import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PauseCircle, ExternalLink, History, Loader2 } from "lucide-react";

import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { RoadmapLink } from "@/components/RoadmapLink";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const STATUS_BADGE = {
  parada: "border-slate-300 bg-slate-100 text-slate-700",
  en_revision: "border-amber-300 bg-amber-50 text-amber-800",
  validada: "border-emerald-300 bg-emerald-50 text-emerald-800",
  activa: "border-emerald-300 bg-emerald-50 text-emerald-800",
};
const STATUS_LABEL = { parada: "Parada", en_revision: "En revisión", validada: "Validada", activa: "Activa" };

const RUN_DOT = { ok: "bg-emerald-500", warning: "bg-amber-500", error: "bg-rose-500" };

function LastRun({ run }) {
  if (!run || (!run.at && !run.summary)) return <span className="text-xs text-muted-foreground">Sin ejecuciones registradas</span>;
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5 text-xs">
        <span className={`h-2 w-2 rounded-full ${RUN_DOT[run.status] || "bg-slate-400"}`} />
        <span className="font-medium">{fmtDT(run.at)}</span>
        {run.by && <span className="text-muted-foreground">· {run.by}</span>}
      </div>
      {run.summary && <div className="text-[11px] text-muted-foreground">{run.summary}</div>}
    </div>
  );
}

function GroupTable({ group, items }) {
  return (
    <Card data-testid={`automations-group-${group}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{group}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[26%]">Automatización</TableHead>
              <TableHead className="w-[20%]">Cuándo corría</TableHead>
              <TableHead className="w-[10%]">Estado</TableHead>
              <TableHead className="w-[24%]">Última ejecución conocida</TableHead>
              <TableHead>Ejecución manual</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((a) => (
              <TableRow key={a.id} data-testid={`automation-row-${a.id}`}>
                <TableCell className="align-top">
                  <div className="font-medium">{a.name}</div>
                  <div className="text-[11px] text-muted-foreground">{a.what}</div>
                </TableCell>
                <TableCell className="align-top text-xs text-muted-foreground">{a.schedule}</TableCell>
                <TableCell className="align-top">
                  <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[a.status] || ""}`} data-testid={`automation-status-${a.id}`}>
                    {STATUS_LABEL[a.status] || a.status}
                  </Badge>
                </TableCell>
                <TableCell className="align-top"><LastRun run={a.last_run} /></TableCell>
                <TableCell className="align-top text-xs">
                  {a.module ? (
                    <Link to={`/app/${a.module}`} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`automation-manual-link-${a.id}`}>
                      {a.manual_label}<ExternalLink className="h-3 w-3" />
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">{a.manual_label}</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function RecentRuns({ runs }) {
  return (
    <Card data-testid="automations-recent-runs">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" />Registro de ejecuciones manuales</CardTitle>
        <CardDescription className="text-xs">Cada vez que alguien lanza una tarea con un botón queda anotado aquí (quién, cuándo, resultado). Base para la revisión una a una.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {runs.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-muted-foreground" data-testid="automations-runs-empty">Todavía no hay ejecuciones manuales registradas.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Automatización</TableHead>
                <TableHead>Quién</TableHead>
                <TableHead>Resultado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id} data-testid={`automation-run-${r.id}`}>
                  <TableCell className="text-xs">{fmtDT(r.at)}</TableCell>
                  <TableCell className="text-xs font-medium">{r.automation_name}</TableCell>
                  <TableCell className="text-xs">{r.user_name || r.triggered_by}</TableCell>
                  <TableCell className="text-xs">
                    <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${RUN_DOT[r.status] || "bg-slate-400"}`} />
                    {r.summary || r.status}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default function AutomatizacionesPage() {
  const q = useQuery({
    queryKey: ["automations-panel"],
    queryFn: async () => (await api.get("/automations")).data,
    refetchInterval: 60000,
  });

  if (q.isLoading) return <div className="space-y-4"><Skeleton className="h-20 w-full" /><Skeleton className="h-64 w-full" /></div>;
  if (q.isError) return <p className="text-sm text-rose-600" data-testid="automations-error">No se pudo cargar el panel de automatizaciones.</p>;

  const d = q.data;
  const groups = [];
  for (const it of d.items) {
    let g = groups.find((x) => x.name === it.group);
    if (!g) { g = { name: it.group, items: [] }; groups.push(g); }
    g.items.push(it);
  }

  return (
    <div className="space-y-5" data-testid="automatizaciones-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <PauseCircle className="h-6 w-6 text-primary" />Automatizaciones
          </h1>
          <p className="text-sm text-muted-foreground">Las {d.total} tareas automáticas de SOFI 2.0, su estado y su última ejecución conocida. Solo se ejecuta lo que una persona pida con un botón.</p>
        </div>
        <RoadmapLink />
      </div>

      <div
        className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm ${d.enabled ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"}`}
        data-testid="automations-banner"
      >
        {q.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <PauseCircle className="h-4 w-4" />}
        {d.enabled ? (
          <span>Las automatizaciones están <b>activas</b>.</span>
        ) : (
          <span>
            Todas las automatizaciones están <b>paradas</b>{d.stopped_since ? <> desde el <b>{fmtDT(d.stopped_since)}</b></> : null}.
            Se reactivan una a una tras su revisión.
          </span>
        )}
      </div>

      {!d.email_sending_enabled && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100" data-testid="email-blocked-banner">
          <PauseCircle className="h-4 w-4" />
          <span>El <b>envío de correos (Resend)</b> está bloqueado en toda la plataforma hasta nuevo aviso (<code>EMAIL_SENDING_ENABLED=false</code>). Los correos que se habrían enviado quedan anotados: <b>{d.emails_blocked}</b> hasta ahora.</span>
        </div>
      )}

      {groups.map((g) => <GroupTable key={g.name} group={g.name} items={g.items} />)}

      <RecentRuns runs={d.recent_runs || []} />
    </div>
  );
}
